import React, { useState } from "react";
import { connect } from "react-redux";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import InputAdornment from "@mui/material/InputAdornment";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import LockIcon from "@mui/icons-material/Lock";
import VpnKeyIcon from "@mui/icons-material/VpnKey";

import { StoreState } from "../redux/configure-store";
import Loading from "../common/components/Loading";
import { submitChangePasswordForm } from "./authentication-actions";
import { isValidPassword, passwordRequirements } from "../common/utils";
import { backgrounds } from "../theme/theme";

interface ChangePasswordPageProps {
  isSubmittingChangePasswordForm: boolean;
  submitChangePasswordForm: (
    currentPassword: string,
    newPassword: string,
    confirmNewPassword: string
  ) => void;
}

function ChangePasswordPage(props: ChangePasswordPageProps) {
  const { isSubmittingChangePasswordForm, submitChangePasswordForm } = props;

  const [data, setData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmNewPassword: "",
  });
  const [errors, setErrors] = useState({
    currentPassword: "",
    newPassword: "",
    confirmNewPassword: "",
  });

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors = {
      currentPassword: "",
      newPassword: "",
      confirmNewPassword: "",
    };

    if (!data.currentPassword) {
      newErrors.currentPassword = "Current password is required";
    }

    if (!isValidPassword(data.newPassword)) {
      newErrors.newPassword = "Password does not meet requirements";
    }

    if (data.newPassword !== data.confirmNewPassword) {
      newErrors.confirmNewPassword = "Passwords do not match";
    }

    setErrors(newErrors);

    if (
      newErrors.currentPassword ||
      newErrors.newPassword ||
      newErrors.confirmNewPassword
    ) {
      return;
    }

    submitChangePasswordForm(
      data.currentPassword,
      data.newPassword,
      data.confirmNewPassword
    );
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: backgrounds.page,
        py: 4,
        px: 2,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          width: "100%",
          maxWidth: 450,
          p: { xs: 3, sm: 4 },
          borderRadius: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Avatar
            sx={{
              width: 56,
              height: 56,
              bgcolor: "primary.main",
              mb: 2,
            }}
          >
            <VpnKeyIcon sx={{ fontSize: 28 }} />
          </Avatar>
          <Typography component="h1" variant="h5" fontWeight={600}>
            Change Password
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Update your account password
          </Typography>
        </Box>

        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            id="currentPassword"
            name="currentPassword"
            label="Current Password"
            type="password"
            autoFocus
            value={data.currentPassword}
            onChange={onChange}
            error={!!errors.currentPassword}
            helperText={errors.currentPassword}
            fullWidth
            required
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon color="action" fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            id="newPassword"
            name="newPassword"
            label="New Password"
            type="password"
            value={data.newPassword}
            onChange={onChange}
            error={!!errors.newPassword}
            helperText={errors.newPassword || passwordRequirements}
            fullWidth
            required
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon color="action" fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            id="confirmNewPassword"
            name="confirmNewPassword"
            label="Confirm New Password"
            type="password"
            value={data.confirmNewPassword}
            onChange={onChange}
            error={!!errors.confirmNewPassword}
            helperText={errors.confirmNewPassword}
            fullWidth
            required
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon color="action" fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            size="large"
            sx={{
              mt: 3,
              py: 1.5,
              fontWeight: 600,
            }}
          >
            {isSubmittingChangePasswordForm ? <Loading /> : "Change Password"}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}

const mapStateToProps = (storeState: StoreState) => ({
  isSubmittingChangePasswordForm:
    storeState.authentication.isSubmittingChangePasswordForm,
});

export default connect(mapStateToProps, { submitChangePasswordForm })(
  ChangePasswordPage
);
